import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import type { ApiAdminCategory } from '../../core/api/api.types';
import { ReferenceApiService } from '../../core/api/reference-api.service';

const VERTICAL_LABELS: Record<string, string> = {
  auto:     'Auto',
  beauty:   'Beauty',
  cleaning: 'Cleaning',
  home:     'Home',
};

@Component({
  selector: 'op-service-category-detail-page',
  imports: [RouterModule, MatIconModule, MatProgressSpinnerModule],
  templateUrl: './service-category-detail.page.html',
  styleUrl: './service-category-detail.page.scss',
})
export class ServiceCategoryDetailPage implements OnInit {
  private readonly location  = inject(Location);
  private readonly route     = inject(ActivatedRoute);
  private readonly router    = inject(Router);
  private readonly referenceApi = inject(ReferenceApiService);

  readonly categoryId = this.route.snapshot.paramMap.get('id') ?? '';

  readonly category = signal<ApiAdminCategory | null>(null);
  readonly loading  = signal(true);
  readonly error    = signal<string | null>(null);

  readonly activeCities = computed(() =>
    this.category()?.city_availability.filter(c => c.is_active) ?? [],
  );

  readonly inactiveCities = computed(() =>
    this.category()?.city_availability.filter(c => !c.is_active) ?? [],
  );

  async ngOnInit(): Promise<void> {
    try {
      const res = await this.referenceApi.adminCategories();
      const found = res.items.find(c => c.category_id === this.categoryId);
      if (!found) {
        this.error.set('Service category not found');
      } else {
        this.category.set(found);
      }
    } catch (err: any) {
      this.error.set(err?.message ?? 'Failed to load service category');
    } finally {
      this.loading.set(false);
    }
  }

  back(): void {
    this.location.back();
  }

  viewProviders(): void {
    this.router.navigate(['/providers'], {
      queryParams: { categoryIds: this.categoryId },
    });
  }

  verticalLabel(vertical: string): string {
    return VERTICAL_LABELS[vertical] ??
      vertical.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }

  formatDate(iso: string): string {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('en-CA', {
        year: 'numeric', month: 'short', day: 'numeric',
      });
    } catch {
      return '—';
    }
  }
}